<template>
  <span
    v-if="loading"
    class="form-control disabled">
    Loading...
  </span>
  <span
    v-else-if="internalError"
    class="form-control disabled border-danger text-danger">
    {{ internalError }}
  </span>
  <options-input-field
    v-else-if="!dropdown"
    :value="computedValue"
    :options="filteredOptions"
    :multiple="multiple"
    :disabled="disabled"
    :item2option="item => ({
      text: labelFor(item),
      value: item[idField],
      help: helpField && item[helpField],
      tags: tagsFor(item)
    })"
    :value2id="item => item[idField]"
    :id2value="id => filteredOptions.find(item => item && item[idField] === id)"
    @input="valueChanged($event)"
  />
  <span
    v-else-if="disabled || computedValue && !find(computedValue)"
    :class="plaintext ? undefined : 'form-control disabled'"
    :style="plaintext ? undefined : 'overflow: hidden; white-space: nowrap;'">
    <template v-if="multiple">
      <b-badge v-for="item in computedValue">
        {{ labelFor(item) }}
      </b-badge>
    </template>
    <template v-else>
      {{ labelFor(computedValue) }}
      <tag-list :value="tagsFor(find(computedValue))" />
    </template>
  </span>
  <div v-else-if="multiple && sortable">
    <div class="table table-striped">
      <div class="tbody" v-if="computedValue.length">
        <div v-for="(item, index) in computedValue" class="row">
          <div class="col-1">
            <b-button variant="link" size="sm" @click.prevent="moveUp(index)">
              <i class="text-info fa fa-fw fa-arrow-up"></i>
            </b-button>
            <b-button variant="link" size="sm" @click.prevent="moveDown(index)">
              <i class="text-info fa fa-fw fa-arrow-down"></i>
            </b-button>
            <b-button variant="link" size="sm" @click.prevent="remove(index)">
              <i class="text-danger fa fa-fw fa-times"></i>
            </b-button>
          </div>
          <div class="col">{{ labelFor(item) }}</div>
        </div>
      </div>
      <multiselect
        :track-by="idField"
        :custom-label="labelFor"
        :options="missingOptions"
        @input="addOption($event)">
      </multiselect>
    </div>
  </div><!-- multiple && sortable -->
  <multiselect
    v-else-if="dropdown"
    :value="find(computedValue)"
    :track-by="idField"
    :custom-label="labelFor"
    :options="filteredOptions"
    :multiple="multiple"
    :class="{ 'is-invalid': error }"
    @input="valueChanged($event)">

    <template
      slot="singleLabel"
      slot-scope="props">
      <!--
      <img
        class="option__image"
        :src="props.option.img"
        alt="No Man’s Sky">-->
      <div class="option__desc">
        <span class="option__title">
          {{ labelFor(props.option) }}
          <tag-list :value="tagsFor(props.option)" />
        </span>
        <!--<span class="option__small">{{ props.option.desc }}</span>-->
      </div>
    </template>
    <template
      slot="option"
      slot-scope="props">
      <!--
      <img
        class="option__image"
        :src="props.option.img"
        alt="No Man’s Sky">-->
      <div class="option__desc">
        <span class="option__title">
          {{ labelFor(props.option) }}
          <tag-list :value="tagsFor(props.option)" />
        </span>
        <!--<span class="option__small">{{ props.option.desc }}</span>-->
      </div>
    </template>
    <template v-slot:tag="{ option }"><slot name="tag" :option="option"></slot></template>
  </multiselect>

</template>
<style lang="scss" src="./EntityInputField.scss"></style>
<script>
import FormFieldMixin from 'vue-forms/mixins/FormFieldMixin';
import EntityFieldMixin from 'vue-forms/mixins/EntityFieldMixin';
import Multiselect from 'vue-multiselect';

export default {
  mixins: [ FormFieldMixin, EntityFieldMixin ],

  components: { Multiselect },

  props: {
    // TODO this should be a system-wide property
    resultsField: {
      type: String,
      default: 'content'
    },

    idField: {
      type: String,
      default: 'id'
    },

    helpField: {
      type: String,
      default: undefined
    },

    service: {
      type: Object,
      required: true
    },
  },

  data () {
    return {
      loading: true,
      internalError: null,
      options: []
    }
  },

  watch: {
    parameters(newValue, oldValue) {
      if (oldValue !== newValue) {
        //console.log('Loading options (watch parameters)');
        this.loadOptions();
      }
    }
  },

  computed: {
    missingOptions() {
      return this.options.filter(item => !this.value.find(v => this.item2id(v) == this.item2id(item)));
    },

    filteredOptions() {
      return this.filter ? this.options.filter(this.filter) : this.options;
    },

    computedValue() {
      if (this.inputId) {
        return this.findById(this.value);
      } else {
        return this.value;
      }
    }
  },

  mounted() {
    //console.log('Loading options (mounted)');
    this.loadOptions();
  },

  methods: {
    valueChanged(value) {
      //console.log('value changed', value);
      if (this.outputId) {
        if (this.multiple) {
          value = value.map(item => this.item2id(item));
        } else {
          value = this.item2id(value);
        }
      }
      this.$emit('input', value);
    },

    item2id(value) {
      return value && value[this.idField];
    },

    customLabel(value) {
      return value;
    },

    labelFor(value) {
      return value && this.customLabel(value);
    },

    customTags(value) {
      return [];
    },

    tagsFor(value) {
      return value && this.customTags(value);
    },

    findById(id) {
      if (Array.isArray(id)) {
        return this.options.filter(item => id.find(x => x === this.item2id(item)));
      } else {
        return this.options.find(item => this.item2id(item) == id);
      }
    },

    find(value) {
      if (Array.isArray(value)) {
        return this.options.filter(item => value.find(x => this.item2id(x) === this.item2id(item)));
      } else {
        return this.options.find(item => this.item2id(item) == this.item2id(value));
      }
    },

    loadOptions() {
      this.loadOptionsDefault();
    },

    loadOptionsDefault() {
      this.internalError = null;
      this.loading = true;

      const success = ({ statusCode, data }) => {
        //console.log('data', data);
        this.options = data[this.resultsField];
        this.loading = false;
      };

      const error = ({ statusCode, data }) => {
        this.internalError = 'Cannot load options';
        this.loading = false;
      }

      this.service.find({
        parameters: this.parameters,
        pageRequest: null,
        sort: this.sort
      }).then(success, error);
    },

    moveUp(index) {
      if (index < 1) {
        return;
      }

      this.swap(index, index - 1);
    },

    moveDown(index) {
      if (index > this.value.length - 2) {
        return;
      }
      this.swap(index, index + 1);
    },

    swap(index1, index2) {
      if (index1 < 0 || index1 >= this.value.length) {
        throw Error('index1 out of bounds');
      }

      if (index2 < 0 || index2 >= this.value.length) {
        throw Error('index2 out of bounds');
      }

      const newValue = [ ...this.value ];
      const aux = newValue[index1];
      newValue[index1] = newValue[index2];
      newValue[index2] = aux;

      this.$emit('input', newValue);
    },

    remove(index) {
      if (index < 0 || index >= this.value.length) {
        throw Error('index out of bounds');
      }
      const newValue = [ ...this.value ];
      newValue.splice(index, 1);
      this.$emit('input', newValue);
    },

    addOption(opt) {
      const newValue = [ ...this.value ];
      newValue.push(opt);
      this.$emit('input', newValue);
    }
  }
};
</script>
